/**
 * Dynamic Tasting Guide
 * Fetches personalized tasting guide data via App Proxy API,
 * then renders the guide using the same HTML structure as static Liquid templates.
 */
(function () {
  'use strict';

  const APP_PROXY_BASE = '/apps/box-builder/tasting-guide';

  // ===== DOM References =====
  const container = document.getElementById('dynamic-tasting-guide');
  if (!container) return;

  const emailSection = container.querySelector('[data-tg-email]');
  const loadingSection = container.querySelector('[data-tg-loading]');
  const errorSection = container.querySelector('[data-tg-error]');
  const guideSection = container.querySelector('[data-tg-guide]');
  const emailForm = container.querySelector('[data-tg-email-form]');
  const emailInput = container.querySelector('[data-tg-email-input]');
  const emailError = container.querySelector('[data-tg-email-error]');
  const errorMessage = container.querySelector('[data-tg-error-message]');
  const retryBtn = container.querySelector('[data-tg-retry]');

  // ===== State Management =====
  function showState(state) {
    emailSection.style.display = state === 'email' ? '' : 'none';
    loadingSection.style.display = state === 'loading' ? '' : 'none';
    errorSection.style.display = state === 'error' ? '' : 'none';
    guideSection.style.display = state === 'guide' ? '' : 'none';
  }

  function showError(code, message) {
    const messages = {
      CUSTOMER_NOT_FOUND: "We couldn't find an account with that email. Please double-check and try again.",
      NO_FULFILLED_ORDERS: "We couldn't find a shipped order for your account yet. Your tasting guide will be available once your order ships!",
      NO_GUIDE_AVAILABLE: "We don't have a tasting guide ready for your most recent box yet. Check back soon!",
    };
    errorMessage.textContent = messages[code] || message || 'Something went wrong. Please try again.';
    showState('error');
  }

  // ===== API Calls =====
  async function lookupByEmail(email) {
    showState('loading');
    try {
      const res = await fetch(APP_PROXY_BASE + '/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        renderGuide(data);
      } else {
        showError(data.error, data.message);
      }
    } catch (e) {
      showError('NETWORK_ERROR', 'Unable to connect. Please check your connection and try again.');
    }
  }

  async function autoLoad() {
    showState('loading');
    try {
      const res = await fetch(APP_PROXY_BASE + '/auto');
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          renderGuide(data);
          return;
        }
      }
    } catch (e) {
      // ignore — fall through to email form
    }
    showState('email');
  }

  // ===== Event Listeners =====
  if (emailForm) {
    emailForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = emailInput.value.trim();
      if (!email) {
        emailError.textContent = 'Please enter your email address.';
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        emailError.textContent = 'Please enter a valid email address.';
        return;
      }
      emailError.textContent = '';
      lookupByEmail(email);
    });
  }

  if (retryBtn) {
    retryBtn.addEventListener('click', function () {
      showState('email');
    });
  }

  // ===== Rendering =====
  function renderGuide(data) {
    var html = '';

    // --- Hero section ---
    if (data.guide.title) {
      html += renderHeroSection(data.guide.title, data.guide.subtitle);
    }

    // --- Free Pairings section ---
    if (data.freePairings && data.freePairings.length > 0) {
      html += renderCategoryDivider(
        'Free Artisan Pairing',
        data.guide.title
          ? 'Your personalized free pairing from your ' + escapeHtml(data.guide.title) + '.'
          : 'Your personalized free pairing, selected just for you.'
      );
      html += renderProductSection(data.freePairings, 'bubbles');
    }

    // --- Main products split into 3 category sections ---
    if (data.mainProducts && data.mainProducts.length > 0) {
      var cheeses = data.mainProducts.filter(function (p) { return p.category === 'cheese'; });
      var meats = data.mainProducts.filter(function (p) { return p.category === 'meat'; });
      var pairings = data.mainProducts.filter(function (p) {
        return p.category === 'cracker' || p.category === 'jam' || p.category === 'other';
      });

      if (cheeses.length > 0) {
        html += renderCategoryDivider('Curated Cheeses', 'Hand-selected artisanal cheeses from small-scale producers.');
        html += renderProductSection(cheeses, 'stacked');
      }

      if (meats.length > 0) {
        html += renderCategoryDivider('Charcuterie', 'Expertly sourced cured meats crafted to complement your cheese selections.');
        html += renderProductSection(meats, 'stacked');
      }

      if (pairings.length > 0) {
        html += renderCategoryDivider('Pairings &amp; Crackers', 'The perfect accompaniments to complete your tasting experience.');
        html += renderProductSection(pairings, 'stacked');
      }
    }

    // --- Reviews / Accompaniments carousel ---
    if (data.guide.accompaniments) {
      html += renderAccompaniments(data.guide.accompaniments);
    }

    // --- Drink Pairings ---
    if (data.guide.drinkPairings) {
      html += renderDrinkPairings(data.guide.drinkPairings);
    }

    // --- Closing ---
    html += '<div class="tg-closing">';
    html += '<div class="tg-closing__rule"></div>';
    html += '<p class="tg-closing__text">Enjoy your tasting experience.</p>';
    html += '</div>';

    guideSection.innerHTML = html;
    showState('guide');

    requestAnimationFrame(function () {
      initBubblesInteraction();
      initSliders();
    });
  }

  // ===== Section Renderers =====

  function renderHeroSection(title, description) {
    var html = '<div class="tg-hero">';
    html += '<div class="tg-hero__inner">';
    html += '<p class="tg-hero__eyebrow">Your Personalized Tasting Guide</p>';
    html += '<h1 class="tg-hero__title">' + escapeHtml(title) + '</h1>';
    html += '<div class="tg-hero__rule"></div>';
    if (description) {
      html += '<p class="tg-hero__desc">' + escapeHtml(description) + '</p>';
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  function renderCategoryDivider(label, desc) {
    var html = '<div class="tg-section">';
    html += '<div class="tg-section__header">';
    html += '<span class="tg-section__line"></span>';
    html += '<span class="tg-section__diamond">&#9670;</span>';
    html += '<span class="tg-section__label">' + label + '</span>';
    html += '<span class="tg-section__diamond">&#9670;</span>';
    html += '<span class="tg-section__line"></span>';
    html += '</div>';
    if (desc) {
      html += '<p class="tg-section__desc">' + desc + '</p>';
    }
    html += '</div>';
    return html;
  }

  // Matches ctr-tasting-guides-products section: schema "class": "section tasting-guides-products"
  function renderProductSection(products, style) {
    var html = '<section class="section tasting-guides-products">';
    html += '<div class="tasting-guides-products__container">';

    // Bubbles selector
    if (style === 'bubbles') {
      html += '<div class="tasting-guides-products__products-bubbles">';
      products.forEach(function (product, index) {
        var id = slugify(product.title) + '-' + index;
        var imgUrl = product.images && product.images.length > 0 ? product.images[0].url : '';
        var imgAlt = product.images && product.images.length > 0 && product.images[0].altText ? product.images[0].altText : product.title;
        html += '<div class="tasting-guides-products__product-bubble' + (index === 0 ? ' active' : '') + '" data-product-bubble data-product-id="' + id + '">';
        html += '<div class="tasting-guides-products__product-bubble-image-wrapper">';
        if (imgUrl) {
          html += '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(imgAlt) + '" class="tasting-guides-products__product-bubble-image" loading="lazy" />';
        }
        html += '</div>';
        html += '<div class="tasting-guides-products__product-bubble-title">' + escapeHtml(cleanTitle(product.title)) + '</div>';
        html += '</div>';
      });
      html += '</div>';
    }

    // Product cards
    var stackedClass = style === 'stacked' ? ' tasting-guides-products__products--stacked' : '';
    html += '<div class="tasting-guides-products__products' + stackedClass + '">';
    products.forEach(function (product, index) {
      html += renderProductCard(product, index, style);
    });
    html += '</div>';

    html += '</div>';
    html += '</section>';
    return html;
  }

  function renderProductCard(product, index, style) {
    var id = slugify(product.title) + '-' + index;
    var hasMultipleImages = product.images && product.images.length > 1;
    var visibilityClass = style === 'bubbles' ? (index === 0 ? ' active' : ' hidden') : '';
    var sliderClass = hasMultipleImages ? ' tasting-guides-products__product--slider' : '';
    var html = '<div class="tasting-guides-products__product' + sliderClass + visibilityClass + '" data-product-id="' + id + '">';

    // Image gallery
    if (product.images && product.images.length > 0) {
      html += '<div class="tasting-guides-products__product-gallery" data-slideshow-classic>';
      product.images.forEach(function (img) {
        html += '<div class="tasting-guides-products__product-gallery-image-wrapper">';
        html += '<img src="' + escapeHtml(img.url) + '" alt="' + escapeHtml(img.altText || product.title) + '" class="tasting-guides-products__product-gallery-image-wrapper-image" loading="lazy" />';
        html += '</div>';
      });
      html += '</div>';
    }

    // Product info
    html += '<div class="tasting-guides-products__product-info">';

    // Top section: title/subtitle + location
    html += '<div class="tasting-guides-products__product-info-top">';
    html += '<div class="tasting-guides-products__product-info-title-wrapper">';
    html += '<h3 class="tasting-guides-products__product-title">' + escapeHtml(cleanTitle(product.title)) + '</h3>';
    if (product.subtitle) {
      html += '<p class="tasting-guides-products__product-subtitle">' + escapeHtml(product.subtitle) + '</p>';
    }
    html += '</div>';

    if (product.location) {
      html += '<div class="tasting-guides-products__product-info-location">';
      html += '<div class="tasting-guides-products__product-info-location-icon">' + locationSvg() + '</div>';
      html += '<div class="tasting-guides-products__product-info-location-text">' + escapeHtml(product.location) + '</div>';
      html += '</div>';
    }
    html += '</div>'; // end top

    // Mild-to-bold scale
    if (product.scalePercentage != null) {
      html += '<div class="tasting-guides-products__product-info-scale-wrapper">';
      html += '<div class="tasting-guides-products__product-info-scale-title">Mild</div>';
      html += '<div class="tasting-guides-products__product-info-scale-track">';
      html += '<div class="tasting-guides-products__product-info-scale-track-bar" style="left: ' + product.scalePercentage + '%;"></div>';
      html += '</div>';
      html += '<div class="tasting-guides-products__product-info-scale-title">Bold</div>';
      html += '</div>';
    }

    // Description
    if (product.description) {
      html += '<div class="tasting-guides-products__product-description rte">' + richTextToHtml(product.description) + '</div>';
    }

    // Best paired with
    if (product.bestPairedWith && product.bestPairedWith.length > 0) {
      html += '<div class="tasting-guides-products__product-pairing-stickers">';
      html += '<div class="tasting-guides-products__product-pairing-stickers-title">Best paired with:</div>';
      html += '<div class="tasting-guides-products__product-pairing-stickers-items">';
      product.bestPairedWith.forEach(function (pairing) {
        html += '<div class="tasting-guides-products__product-pairing-sticker">' + escapeHtml(pairing) + '</div>';
      });
      html += '</div></div>';
    }

    // Ingredients accordion ("More Details")
    if (product.ingredients) {
      var accordionId = 'tg-accordion-' + id;
      html += '<div class="tasting-guides-products__product-accordions">';
      html += '<div class="tasting-guides-products__product-accordion">';
      html += '<input type="checkbox" name="tasting-guides-products__product-accordion" id="' + accordionId + '" class="hidden tasting-guides-products__product-accordion-input">';
      html += '<label for="' + accordionId + '" class="tasting-guides-products__product-accordion-label">More Details</label>';
      html += '<div class="tasting-guides-products__product-accordion-description-wrapper">';
      html += '<div class="tasting-guides-products__product-accordion-description">' + escapeHtml(product.ingredients) + '</div>';
      html += '</div>';
      html += '</div></div>';
    }

    html += '</div>'; // end product-info
    html += '</div>'; // end product

    return html;
  }

  function renderAccompaniments(accompaniments) {
    if (!accompaniments.reviews || accompaniments.reviews.length === 0) return '';

    var html = '';
    if (accompaniments.heading) {
      html += renderCategoryDivider(
        escapeHtml(accompaniments.heading),
        accompaniments.subheading ? escapeHtml(accompaniments.subheading) : ''
      );
    }

    html += '<div class="tg-accompaniments">';
    html += '<div class="tg-accompaniments__carousel">';
    accompaniments.reviews.forEach(function (review) {
      html += '<div class="tg-accompaniments__card">';
      if (review.image) {
        html += '<div class="tg-accompaniments__card-image"><img src="' + escapeHtml(review.image) + '" alt="" loading="lazy" /></div>';
      }
      html += '<h4 class="tg-accompaniments__card-headline">' + escapeHtml(review.headline) + '</h4>';
      html += '<p class="tg-accompaniments__card-review">' + escapeHtml(review.review) + '</p>';
      html += '</div>';
    });
    html += '</div>';
    html += '</div>';
    return html;
  }

  function renderDrinkPairings(drinkPairingsHtml) {
    var html = renderCategoryDivider(
      'Drink Pairings',
      'While drink pairings are ultimately a matter of personal preference, we invite you to consider our recommended pairings as a starting point.'
    );
    html += '<div class="tg-drink-pairings"><div class="tg-drink-pairings__content">';
    html += richTextToHtml(drinkPairingsHtml);
    html += '</div></div>';
    return html;
  }

  // ===== Post-render Interactions =====

  function initBubblesInteraction() {
    var bubblesContainers = guideSection.querySelectorAll('.tasting-guides-products__products-bubbles');
    bubblesContainers.forEach(function (bubblesEl) {
      var parentContainer = bubblesEl.closest('.tasting-guides-products__container');
      if (!parentContainer) return;

      var bubbles = bubblesEl.querySelectorAll('[data-product-bubble]');
      var products = parentContainer.querySelectorAll('.tasting-guides-products__product');

      bubbles.forEach(function (bubble) {
        bubble.addEventListener('click', function () {
          var productId = bubble.dataset.productId;

          bubbles.forEach(function (b) { b.classList.remove('active'); });
          bubble.classList.add('active');

          products.forEach(function (p) {
            p.classList.remove('active');
            p.classList.add('hidden');
          });

          var target = parentContainer.querySelector('.tasting-guides-products__product[data-product-id="' + productId + '"]');
          if (target) {
            target.classList.remove('hidden');
            target.classList.add('active');
          }
        });
      });
    });
  }

  function initSliders() {
    if (typeof Flickity === 'undefined') return;

    var sliders = guideSection.querySelectorAll('.tasting-guides-products__product--slider [data-slideshow-classic]');
    sliders.forEach(function (slider) {
      new Flickity(slider, {
        wrapAround: true,
        adaptiveHeight: true,
        prevNextButtons: false,
        pageDots: true,
        draggable: true,
        imagesLoaded: true,
        fade: true,
      });
    });
  }

  // ===== Helpers =====

  function richTextToHtml(jsonStr) {
    try {
      var doc = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
      if (!doc || !doc.children) return '';
      return doc.children.map(renderRichNode).join('');
    } catch (e) {
      return escapeHtml(jsonStr);
    }
  }

  function renderRichNode(node) {
    if (!node) return '';
    if (node.type === 'text') return escapeHtml(node.value || '');
    var inner = (node.children || []).map(renderRichNode).join('');
    switch (node.type) {
      case 'root': return inner;
      case 'paragraph': return '<p>' + inner + '</p>';
      case 'heading':
        var level = node.level || 3;
        return '<h' + level + '>' + inner + '</h' + level + '>';
      case 'list':
        var tag = node.listType === 'ordered' ? 'ol' : 'ul';
        return '<' + tag + '>' + inner + '</' + tag + '>';
      case 'list-item': return '<li>' + inner + '</li>';
      case 'bold': return '<strong>' + inner + '</strong>';
      case 'italic': return '<em>' + inner + '</em>';
      case 'link':
        return '<a href="' + escapeHtml(node.url || '') + '">' + inner + '</a>';
      default: return inner;
    }
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function cleanTitle(str) {
    return str ? str.replace(/\s*\*+/g, '') : '';
  }

  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function locationSvg() {
    return '<svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.625 17.1875H10.557C10.9617 16.7969 11.3969 16.3508 11.8328 15.8492C14.0273 13.3258 15.1875 10.6539 15.1875 8.125C15.1875 6.21876 14.4302 4.39059 13.0823 3.04267C11.7344 1.69475 9.90624 0.9375 8 0.9375C6.09376 0.9375 4.26559 1.69475 2.91767 3.04267C1.56975 4.39059 0.8125 6.21876 0.8125 8.125C0.8125 12.0312 3.47813 15.3078 5.43594 17.1875H2.375C2.12636 17.1875 1.8879 17.2863 1.71209 17.4621C1.53627 17.6379 1.4375 17.8764 1.4375 18.125C1.4375 18.3736 1.53627 18.6121 1.71209 18.7879C1.8879 18.9637 2.12636 19.0625 2.375 19.0625H13.625C13.8736 19.0625 14.1121 18.9637 14.2879 18.7879C14.4637 18.6121 14.5625 18.3736 14.5625 18.125C14.5625 17.8764 14.4637 17.6379 14.2879 17.4621C14.1121 17.2863 13.8736 17.1875 13.625 17.1875ZM2.6875 8.125C2.6875 6.71604 3.24721 5.36478 4.2435 4.3685C5.23978 3.37221 6.59104 2.8125 8 2.8125C9.40896 2.8125 10.7602 3.37221 11.7565 4.3685C12.7528 5.36478 13.3125 6.71604 13.3125 8.125C13.3125 10.7273 11.75 13.0758 10.4453 14.5867C9.70042 15.4424 8.8822 16.2313 8 16.9445C7.1178 16.2313 6.29958 15.4424 5.55469 14.5867C4.25 13.0758 2.6875 10.7273 2.6875 8.125ZM8 11.5625C8.67987 11.5625 9.34448 11.3609 9.90977 10.9832C10.4751 10.6055 10.9157 10.0686 11.1758 9.44047C11.436 8.81235 11.5041 8.12119 11.3714 7.45438C11.2388 6.78757 10.9114 6.17506 10.4307 5.69432C9.94994 5.21358 9.33743 4.88619 8.67062 4.75355C8.00381 4.62091 7.31265 4.68899 6.68453 4.94916C6.0564 5.20934 5.51954 5.64993 5.14182 6.21523C4.76411 6.78052 4.5625 7.44513 4.5625 8.125C4.56353 9.03636 4.92603 9.91011 5.57046 10.5545C6.21489 11.199 7.08864 11.5615 8 11.5625ZM8 6.5625C8.30903 6.5625 8.61113 6.65414 8.86808 6.82583C9.12503 6.99752 9.3253 7.24155 9.44356 7.52706C9.56182 7.81257 9.59277 8.12673 9.53248 8.42983C9.47219 8.73292 9.32337 9.01133 9.10485 9.22985C8.88633 9.44837 8.60792 9.59719 8.30483 9.65748C8.00173 9.71777 7.68757 9.68682 7.40206 9.56856C7.11655 9.4503 6.87252 9.25003 6.70083 8.99308C6.52914 8.73613 6.4375 8.43403 6.4375 8.125C6.4375 7.7106 6.60212 7.31317 6.89515 7.02015C7.18817 6.72712 7.5856 6.5625 8 6.5625Z" fill="#2D307A"/></svg>';
  }

  // ===== Init =====
  var urlParams = new URLSearchParams(window.location.search);
  var emailParam = urlParams.get('email');

  if (emailParam && emailParam.trim()) {
    lookupByEmail(emailParam.trim());
  } else {
    var isLoggedIn = container.dataset.customerLoggedIn === 'true';
    if (isLoggedIn) {
      autoLoad();
    } else {
      showState('email');
    }
  }
})();
